import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TokenManager from '../../../utils/storage/TokenManager';
import { AIChatAgentIcons } from '../../helpers/iconHelpers';
import './AIChatAgent.sass';

const { AIIcon, HeaderIcon, ExpandIcon, CollapseIcon, NewTabIcon } = AIChatAgentIcons;

const AI_STAGE_URL = 'https://ai-stage.roscolive.com/';
const AI_PROD_URL = 'https://ai.roscolive.com/';
const PROD_HOSTNAME = 'fleet.roscolive.com';

const getAIChatAgentUrl = () => (
	location.hostname === PROD_HOSTNAME ? AI_PROD_URL : AI_STAGE_URL
);

const AIChatAgent = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'aiChatAgent' });

	const [isOpen, setIsOpen] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [frameSrc, setFrameSrc] = useState('');
	const [canExpand, setCanExpand] = useState(() => window.innerWidth > 1600);

	useEffect(() => {
		const onResize = () => {
			const wide = window.innerWidth > 1600;
			setCanExpand(wide);
			if (!wide) setIsExpanded(false);
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	useEffect(() => {
		if (!isOpen || frameSrc) return;

		TokenManager.getSessionToken().then((token) => {
			setFrameSrc(`${getAIChatAgentUrl()}#session_token=${encodeURIComponent(token || '')}`);
		});
	}, [isOpen, frameSrc]);

	useEffect(() => {
		document.body.classList.toggle('ai-chat-agent-open', isOpen);
		document.body.classList.toggle('ai-chat-agent-expanded', isOpen && isExpanded);
		return () => {
			document.body.classList.remove('ai-chat-agent-open', 'ai-chat-agent-expanded');
		};
	}, [isOpen, isExpanded]);

	const openInNewTab = () => {
		if (frameSrc) window.open(frameSrc, '_blank', 'noopener,noreferrer');
	};

	return (
		<div className="ai-chat-agent">
			{isOpen && (
				<div className={`popup${isExpanded ? ' expanded' : ''}`}>
					<div className="main">
						<div className="header">
							<div className="header-left">
								<div className="header-icon-wrap"><HeaderIcon className="header-icon" /></div>
								<div className="header-info">
									<span className="header-title">{t('headerTitle')}</span>
								</div>
							</div>
							<div className="header-actions">
								<button
									className="icon-btn"
									onClick={openInNewTab}
									disabled={!frameSrc}
									aria-label={t('openInNewTab')}
									title={t('openInNewTab')}
								>
									<NewTabIcon />
								</button>
								<button
									className="icon-btn expand-btn"
									onClick={() => canExpand && setIsExpanded(e => !e)}
									disabled={!canExpand}
									aria-label={isExpanded && canExpand ? t('collapse') : t('expand')}
									title={isExpanded && canExpand ? t('collapse') : t('expand')}
								>
									{isExpanded && canExpand ? <CollapseIcon /> : <ExpandIcon />}
								</button>
								<button className="close" onClick={() => setIsOpen(false)} aria-label={t('close')} title={t('close')}>&#x2715;</button>
							</div>
						</div>

						{frameSrc && (
							<div className="ai-frame-wrap">
								<iframe src={frameSrc} title={t('headerTitle')} />
							</div>
						)}
					</div>
				</div>
			)}

			<div className="border-wrap" onClick={() => setIsOpen(o => !o)}>
				<button className="btn">
					<AIIcon />
					<span className="label">{t('label')}</span>
				</button>
			</div>
		</div>
	);
};

export default AIChatAgent;
